/**
 * AuthContext.tsx - Authentication Context
 *
 * Session-level auth state shared across the app: profile, authentication
 * flag and Safari storage-access info. The auth UI itself is the canonical
 * lib-core panel — see ui/views/authentication/Authentication.tsx.
 */

import React, { createContext, useContext, ReactNode } from 'react'
import { AuthContextType } from '@/types/auth.types'
import { useProfile } from 'core'
import { useStorageAccess } from '@/hooks/useStorageAccess'
import { getAvatarUrl } from '@/utilities/imageUtils'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
    children: ReactNode;
    api: any;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    // Initialize storage access state for Safari third-party cookie handling
    const storageAccess = useStorageAccess()

    const { data: profile, isLoading } = useProfile()

    const profileName = profile ? (profile.firstname || profile.username || '') : ''
    // getAvatarUrl carries the localhost→newssocial.uk fallback and shared imgix
    // params, so avatars resolve in local dev too
    const profileImageUrl = profile && profile.avatar && profile.avatar.uuid
        ? getAvatarUrl(profile.avatar)
        : ''

    const value: AuthContextType = {
        profile: profile || null,
        isAuthenticated: !!profile,
        loading: isLoading,
        profileName,
        profileImageUrl,
        storageAccess: {
            hasAccess: storageAccess.hasAccess,
            needsAccess: storageAccess.needsAccess,
            isSupported: storageAccess.isSupported,
            isIframe: storageAccess.isIframe,
            error: storageAccess.error,
        },
        requestStorageAccess: storageAccess.requestAccess,
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuthContext = () => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuthContext must be used within an AuthProvider')
    }
    return context
}
